<template>
  <div v-if="showBar" class="navigation-progress" role="status" :aria-label="$t('general.loading')">
    <div class="track">
      <div class="bar" />
    </div>
    <transition name="fade-hint">
      <span v-if="isSlowNavigation" class="hint">{{ $t('general.takingLongerThanUsual') }}</span>
    </transition>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator'
import { mapState, mapStores } from 'pinia'
import type { Route } from 'vue-router'
import SlowRequestHint from '@/util/SlowRequestHint'
import { isChunkLoadError, onNavigationStart, pendingNavigation } from '@/util/navigationProgress'
import { claimNetworkError } from '@/util/api-error-snackbar'
import { getSpaceIdFromRoute } from '@/router/helpers'
import { useAppStore } from '@/modules/global/stores/AppStore'
import { SnackbarStore, useSnackbarStore } from '@/modules/global/stores/SnackbarStore'

/**
 * Global feedback for route navigation.
 *
 * `tokenCheckGuard` awaits several requests in sequence before a route resolves, and each view is a
 * lazy chunk, so on a slow connection there is a multi-second gap where the previous page is still
 * mounted and nothing at all indicates that a click was registered. This bar covers exactly that
 * gap: click -> immediate progress -> new view.
 *
 * Mounted once in App.vue rather than inside Framework, so it also covers the routes that render
 * outside the app shell (login, register, public space verification) and is not hidden by the boot
 * spinner.
 */
const SHOW_DELAY_MS = 150

/**
 * Hard ceiling so the bar can never be stuck on permanently, which would read as a frozen page just
 * as badly as no feedback. A navigation aborted by a guard fires neither `afterEach` nor `onError`,
 * so those hooks alone are not a complete guarantee. Set well beyond the 10s ApiClient timeout: any
 * real navigation has resolved or failed long before this fires.
 */
const MAX_VISIBLE_MS = 30000

const NAVIGATION_FAILED_SNACKBAR = 'navigation-failed'

@Component({
  computed: {
    ...mapState(useAppStore, ['isInPrintMode']),
    ...mapStores(useSnackbarStore)
  }
})
export default class NavigationProgressBar extends Vue {
  private isInPrintMode!: boolean
  snackbarStore!: SnackbarStore

  private visible = false

  private isSlowNavigation = false

  // Constructed in created(), not as a field initializer: vue-class-component evaluates field
  // initializers against a throwaway instance while collecting data(), so a `this` captured
  // there writes to a discarded object and the flag would never actually flip.
  private slowNavigationHint!: SlowRequestHint

  private showDelay = 0

  private maxVisibleTimeout = 0

  private removeHooks: Array<() => void> = []

  // The navigation in flight, so a failure can offer to repeat exactly it
  private pendingRoute: Route | null = null

  private retrySnackbarId: string | null = null

  // PDF export renders the app with printMode enabled and screenshots it, so navigation
  // feedback must never bleed into an exported document. NSnackbarProvider opts out the same way.
  private get showBar(): boolean {
    return this.visible && !this.isInPrintMode
  }

  created() {
    this.slowNavigationHint = new SlowRequestHint(isSlow => (this.isSlowNavigation = isSlow))
    // The first navigation started before this component existed; App covers it with the boot spinner, but its failure
    // still needs the retry notice, or the user is left with a blank page
    this.pendingRoute = pendingNavigation()

    // The start signal comes from the first router guard (see installNavigationProgress), not from a
    // beforeEach registered here: that would queue behind guards that await the network.
    this.removeHooks.push(
      onNavigationStart((to, from) => {
        this.pendingRoute = to
        try {
          // Switching pages inside one space keeps the same view mounted and is already covered
          // by the element skeleton there, so a second indicator would just be noise.
          if (!this.isSameSpaceNavigation(to, from)) {
            this.start()
          }
        } catch {
          // Showing the bar is the safe default when we cannot classify the navigation.
          this.start()
        }
      })
    )
    // Vue Router 3 returns an unregister function from each global hook.
    this.removeHooks.push(
      this.$router.afterEach(() => {
        this.pendingRoute = null
        this.stop()
        // A navigation went through after all, so the failure notice no longer applies
        if (this.retrySnackbarId) {
          this.snackbarStore.dequeue(this.retrySnackbarId)
          this.retrySnackbarId = null
        }
      })
    )
    // Without this a failed navigation (a rejected guard, a chunk that fails to load) left the bar
    // running forever, which reads as a hung page just as badly as no feedback at all.
    this.$router.onError(error => {
      this.stop()
      this.offerRetry(error)
    })
  }

  /**
   * A failed navigation leaves the previous page in place, which on its own looks like the click was ignored. Say so
   * and offer to repeat it: on a flaky connection the second attempt usually succeeds.
   */
  private offerRetry(error: unknown) {
    const route = this.pendingRoute
    this.pendingRoute = null
    if (!route || this.isInPrintMode) {
      return
    }
    // A timed-out guard request also queued ApiClient's generic connection notice: this one says the same and retries
    claimNetworkError(error)
    // A notice still up from an earlier failure would keep its own route (enqueue dedupes to it): replace it, so
    // Try again repeats the navigation that just failed
    if (this.retrySnackbarId) {
      this.snackbarStore.dequeue(this.retrySnackbarId)
    }
    this.retrySnackbarId = this.snackbarStore.enqueue({
      label: this.$t('general.pageFailedToLoad') as string,
      variant: 'alert',
      leadingIcon: true,
      autoHide: false,
      buttonLabel: this.$t('general.tryAgain') as string,
      onButtonClick: () => {
        if (isChunkLoadError(error)) {
          window.location.assign(route.fullPath)
        } else {
          this.$router.push(route.fullPath).catch(() => undefined)
        }
      },
      dedupeKey: NAVIGATION_FAILED_SNACKBAR
    })
  }

  beforeDestroy() {
    this.removeHooks.forEach(remove => remove())
    this.removeHooks = []
    this.clearShowDelay()
    this.clearMaxVisible()
    this.slowNavigationHint.cancel()
  }

  private isSameSpaceNavigation(to: Route, from: Route): boolean {
    // The slug lookups go through SpaceStore, so they are only reached once both routes are
    // known to be space routes — never on the very first navigation, where `from` has no meta.
    if (!to.meta?.isSpace || !from.meta?.isSpace) {
      return false
    }
    return getSpaceIdFromRoute(to) === getSpaceIdFromRoute(from)
  }

  private start() {
    this.clearShowDelay()
    // A short delay keeps fast navigations flicker-free: on a good connection the route resolves
    // before the bar is ever painted.
    this.showDelay = window.setTimeout(() => {
      this.showDelay = 0
      this.visible = true
      this.slowNavigationHint.start()
      this.maxVisibleTimeout = window.setTimeout(() => this.stop(), MAX_VISIBLE_MS)
    }, SHOW_DELAY_MS)
  }

  private stop() {
    this.clearShowDelay()
    this.clearMaxVisible()
    this.visible = false
    this.slowNavigationHint.cancel()
  }

  private clearShowDelay() {
    if (this.showDelay) {
      window.clearTimeout(this.showDelay)
      this.showDelay = 0
    }
  }

  private clearMaxVisible() {
    if (this.maxVisibleTimeout) {
      window.clearTimeout(this.maxVisibleTimeout)
      this.maxVisibleTimeout = 0
    }
  }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles' as *;

.navigation-progress {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: $notch-layer-navigation-progress;
  pointer-events: none;
}

.track {
  width: 100%;
  height: $notch-space-1x;
  overflow: hidden;
  background-color: $notch-color-neutral-sand-200;
}

// Indeterminate on purpose: neither the router nor the guards expose real progress, so the bar
// signals activity without claiming a percentage it cannot know.
.bar {
  width: 40%;
  height: 100%;
  background-color: $notch-color-neutral-grey-900;
  animation: navigation-progress-slide 1.1s ease-in-out infinite;
}

.hint {
  @include notch-text-label-medium-XS;
  display: block;
  width: fit-content;
  margin: $notch-space-2x auto 0;
  padding: $notch-space-1x $notch-space-3x;
  border-radius: $notch-radius-full;
  background-color: $notch-color-neutral-grey-900;
  color: $notch-color-neutral-grey-000;
}

.fade-hint-enter-active,
.fade-hint-leave-active {
  transition: opacity 0.2s ease;
}

.fade-hint-enter,
.fade-hint-leave-to {
  opacity: 0;
}

@keyframes navigation-progress-slide {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(250%);
  }
}

// Respect a reduced-motion preference: keep the bar, drop the travelling animation.
@media (prefers-reduced-motion: reduce) {
  .bar {
    width: 100%;
    animation: none;
  }
}
</style>
